import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Collapse, IconButton, LinearProgress, Typography } from "@mui/material";
import { IconChevronDown, IconX } from "@tabler/icons";
import { JOB_STAGE_LABELS } from "src/constants/videoConvertStatus";
import mediaService from "src/services/media";
import {
  forgetSession,
  recallSession,
  rememberSession,
  resumeKeyFor,
  uploadResumable,
} from "src/services/resumableUpload";
import { MediaDomain, MediaJob, MediaRole, isTerminal } from "src/types/media";

/**
 * Every upload the panel has in flight, in one place above the routes.
 *
 * A bulk drop is ten files against ten different episodes, and nobody stays
 * on the season page while they go up. `MediaAsset` owns one asset's upload
 * and dies with its screen; this owns a queue and outlives every screen,
 * because the provider is mounted once, outside `<Routes>`.
 *
 * Two at a time. A third parallel upload does not make a 20 Mbit line faster,
 * and ten do make each one's progress bar meaningless.
 *
 * What a tray item shows after the bytes are up is the job's own stage - a
 * catalogue episode encodes on the capped batch lane, so "Waiting for a free
 * encoder (n ahead)" is the normal first state, not an error.
 */

const MAX_IN_FLIGHT = 2;
const POLL_MS = 5000;

export interface UploadAddress {
  domain: MediaDomain;
  /** `[movieId]`, or `[showId, episodeId]` for a TV episode. */
  entity_ids: number[];
  role: MediaRole;
}

export type UploadStatus = "waiting" | "uploading" | "encoding" | "done" | "failed" | "canceled";

export interface UploadItem {
  /** `resumeKeyFor(address)` - one asset, one queue slot, one resumable session. */
  key: string;
  /** What this upload is for, in the editor's words: "S01E03 · Winter Is Coming". */
  label: string;
  fileName: string;
  status: UploadStatus;
  uploaded: number;
  total: number;
  /** Bytes per second, averaged over the upload so far. */
  rate: number;
  job: MediaJob | null;
  error?: string;
}

interface UploadManagerValue {
  items: UploadItem[];
  enqueue: (address: UploadAddress, file: File, label: string) => void;
  cancel: (key: string) => void;
  dismiss: (key: string) => void;
}

const UploadManagerContext = React.createContext<UploadManagerValue>({
  items: [],
  enqueue: () => {},
  cancel: () => {},
  dismiss: () => {},
});

export function useUploadManager(): UploadManagerValue {
  return useContext(UploadManagerContext);
}

interface Queued {
  address: UploadAddress;
  file: File;
}

export function UploadManagerProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const queued = useRef(new Map<string, Queued>());
  const aborts = useRef(new Map<string, AbortController>());
  const running = useRef(new Set<string>());

  const patch = useCallback((key: string, changes: Partial<UploadItem>) => {
    setItems((current) => current.map((item) => (item.key === key ? { ...item, ...changes } : item)));
  }, []);

  const enqueue = useCallback((address: UploadAddress, file: File, label: string) => {
    const key = resumeKeyFor(address.domain, address.entity_ids, address.role);
    queued.current.set(key, { address, file });

    setItems((current) => [
      ...current.filter((item) => item.key !== key),
      {
        key,
        label,
        fileName: file.name,
        status: "waiting",
        uploaded: 0,
        total: file.size,
        rate: 0,
        job: null,
      },
    ]);
  }, []);

  const cancel = useCallback((key: string) => {
    aborts.current.get(key)?.abort();
    queued.current.delete(key);
    setItems((current) =>
      current.map((item) =>
        item.key === key && (item.status === "waiting" || item.status === "uploading")
          ? { ...item, status: "canceled" }
          : item
      )
    );
  }, []);

  const dismiss = useCallback((key: string) => {
    setItems((current) => current.filter((item) => item.key !== key));
  }, []);

  const start = useCallback(
    async (key: string) => {
      const work = queued.current.get(key);
      if (!work) return;

      running.current.add(key);
      const controller = new AbortController();
      aborts.current.set(key, controller);
      const startedAt = Date.now();
      const { address, file } = work;

      patch(key, { status: "uploading", uploaded: 0, rate: 0, error: undefined });

      try {
        const stored = recallSession(key);
        // Same file, same session: carry on rather than re-uploading gigabytes.
        const resuming = Boolean(stored && stored.file_name === file.name);
        const session =
          resuming && stored
            ? stored
            : await mediaService.openUpload({
                domain: address.domain,
                entity_ids: address.entity_ids,
                role: address.role,
                content_type: file.type || "video/mp4",
                size_bytes: file.size,
              });

        rememberSession(key, session, file.name);

        await uploadResumable(file, session, {
          resume: resuming,
          resumeKey: key,
          signal: controller.signal,
          onProgress: (uploaded, total) => {
            const seconds = (Date.now() - startedAt) / 1000;
            patch(key, { uploaded, total, rate: seconds > 0 ? uploaded / seconds : 0 });
          },
        });

        const job = await mediaService.finishUpload(session.upload_id, address);

        patch(key, { status: "encoding", uploaded: file.size, job });
      } catch (error: any) {
        if (controller.signal.aborted) {
          patch(key, { status: "canceled" });
        } else {
          // The queue's own failures are shown on the item, not thrown at a
          // toast: the screen that started this upload is usually long gone.
          patch(key, {
            status: "failed",
            error: error?.response?.data?.message || error?.message || "The upload could not be finished.",
          });
          forgetSession(key);
        }
      } finally {
        queued.current.delete(key);
        aborts.current.delete(key);
        running.current.delete(key);
      }
    },
    [patch]
  );

  // Start whatever the cap allows, whenever a slot frees up.
  useEffect(() => {
    const inFlight = items.filter((item) => item.status === "uploading").length;
    const next = items
      .filter((item) => item.status === "waiting" && !running.current.has(item.key))
      .slice(0, Math.max(0, MAX_IN_FLIGHT - inFlight));

    next.forEach((item) => start(item.key));
  }, [items, start]);

  // Follow every encode this session started until it settles, so the tray
  // says "waiting for a free encoder" and then "encoding 40%" on its own.
  const encoding = items.filter((item) => item.status === "encoding" && item.job);

  useEffect(() => {
    if (encoding.length === 0) return undefined;

    const ulids = encoding.map((item) => ({ key: item.key, ulid: item.job!.job_ulid }));
    const timer = setInterval(() => {
      ulids.forEach(({ key, ulid }) => {
        mediaService
          .detail(ulid)
          .then((job) => {
            if (!isTerminal(job.status)) {
              patch(key, { job });

              return;
            }

            patch(key, { job, status: job.status === "succeeded" ? "done" : "failed" });
            if (job.status === "succeeded") forgetSession(key);
          })
          .catch(() => undefined);
      });
    }, POLL_MS);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encoding.map((item) => `${item.key}:${item.job!.job_ulid}`).join(","), patch]);

  const value = useMemo(
    () => ({ items, enqueue, cancel, dismiss }),
    [items, enqueue, cancel, dismiss]
  );

  return (
    <UploadManagerContext.Provider value={value}>
      {children}
      <UploadTray items={items} onCancel={cancel} onDismiss={dismiss} />
    </UploadManagerContext.Provider>
  );
}

/** What an item is doing, in the same words the media-jobs screen uses. */
export function itemStatusText(item: UploadItem): string {
  switch (item.status) {
    case "waiting":
      return "Waiting to upload";
    case "uploading":
      return `Uploading · ${percentOf(item)}%`;
    case "encoding": {
      const job = item.job;
      if (!job) return "Encoding";

      const stage = job.stage ? JOB_STAGE_LABELS[job.stage] ?? job.stage : null;

      if (job.stage === "waiting_for_capacity") {
        return job.queue_position != null ? `${stage} (${job.queue_position} ahead)` : (stage as string);
      }

      return stage ? `${stage} · ${job.progress}%` : `Encoding · ${job.progress}%`;
    }
    case "done":
      return "Ready";
    case "canceled":
      return "Cancelled";
    default:
      return item.error ?? "Failed";
  }
}

function percentOf(item: UploadItem): number {
  return item.total ? Math.floor((item.uploaded / item.total) * 100) : 0;
}

interface TrayProps {
  items: UploadItem[];
  onCancel: (key: string) => void;
  onDismiss: (key: string) => void;
}

/**
 * The tray. It exists only while something is in it, sits above everything
 * and follows no route - which is the whole point: the dialog that started
 * these uploads closed the moment Confirm was pressed.
 */
function UploadTray({ items, onCancel, onDismiss }: TrayProps) {
  const [open, setOpen] = useState(true);

  if (items.length === 0) return null;

  const active = items.filter((item) => item.status === "uploading" || item.status === "waiting").length;

  return (
    <Box
      aria-label="Uploads"
      sx={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: 1300,
        width: 360,
        maxWidth: "calc(100vw - 32px)",
        bgcolor: "background.paper",
        border: 1,
        borderColor: "grey.200",
        borderRadius: 2,
        boxShadow: "0 12px 32px -8px rgba(16, 24, 40, 0.24)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: open ? 1 : 0,
          borderColor: "grey.100",
        }}
      >
        <Typography sx={{ flex: 1, fontSize: "0.875rem", fontWeight: 600, color: "text.primary" }}>
          {active > 0 ? `Uploading ${active} of ${items.length}` : `${items.length} upload${items.length === 1 ? "" : "s"}`}
        </Typography>
        <IconButton size="small" aria-label={open ? "Collapse uploads" : "Expand uploads"} onClick={() => setOpen((value) => !value)}>
          <Box
            component={IconChevronDown}
            size={16}
            sx={{ transition: "transform 200ms", transform: open ? "none" : "rotate(180deg)" }}
          />
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
          {items.map((item) => (
            <Box key={item.key} sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "grey.100" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.primary" }} noWrap>
                    {item.label}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: item.status === "failed" ? "error.main" : "text.secondary" }} noWrap>
                    {itemStatusText(item)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  aria-label={
                    item.status === "uploading" || item.status === "waiting"
                      ? `Cancel ${item.label}`
                      : `Dismiss ${item.label}`
                  }
                  onClick={() =>
                    item.status === "uploading" || item.status === "waiting"
                      ? onCancel(item.key)
                      : onDismiss(item.key)
                  }
                >
                  <IconX size={14} />
                </IconButton>
              </Box>

              {(item.status === "uploading" || item.status === "encoding") && (
                <LinearProgress
                  variant={item.status === "uploading" ? "determinate" : item.job?.progress ? "determinate" : "indeterminate"}
                  value={item.status === "uploading" ? percentOf(item) : item.job?.progress ?? 0}
                  sx={{ mt: 1, height: 4, borderRadius: 2, bgcolor: "grey.100" }}
                />
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
